import { useCallback } from 'react';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { compose, isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader } from 'components';
import CollapsibleTable from 'components/atoms/GefMui/CollapsibleTable';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import contactList from 'features/views/contactList';
import { loadMore, loadPage, reload } from 'features/views/contacts/actions';
import { getContacts } from 'features/views/contacts/selectors';

import ContactsRow from './TableRow';

const fetchStorePath = `${fetch.constants.urls.contactList}.list`;

export const ContactsTable = ({ solutionStates, idkRegionsCodelist, idkAgenciesCodelist }) => {
	const dispatch = useDispatch();
	const sort = useSelector(contactList.selectors.getSort);
	const fetchState = useSelector(fetch.selectors.getFetchState(fetchStorePath));
	const contacts = useSelector(getContacts('list'));

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore('list')(true));
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, pageIndex) => {
			dispatch(loadPage('list')(pageIndex - 1));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(reload('list'));
	}, [dispatch]);

	const handleSort = useCallback(
		(sort) => {
			dispatch(contactList.actions.sortContacts(sort));
		},
		[dispatch],
	);

	const hasRecords = !isEmpty(contacts);

	return (
		<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReload}>
			<TableContainer>
				<CollapsibleTable
					tableId={contactList.constants.COLLAPSIBLE_TABLE_NAME}
					autoCloseOtherRows
					tableHeader={
						<TableHead>
							<TableRow>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy={['orderingClientName']}
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:client')}
									</SortCell>
								</TableCell>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy={['campaign.campaignName', 'orderingClientName']}
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:campaign')}
									</SortCell>
								</TableCell>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="productName"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:product')}
									</SortCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="policy.policyNo"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:policyNo')}
									</SortCell>
								</TableCell>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="client.personIn"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:personIn')}
									</SortCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="client.businessIn"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:businessIn')}
									</SortCell>
								</TableCell>
								<TableCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="solverName"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:sellerName')}
									</SortCell>
									<SortCell
										hasRecords={hasRecords}
										sortBy="solutionState"
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage('contacts:solutionState')}
									</SortCell>
								</TableCell>
								<TableCell sx={{ px: 1 }} />
								<TableCell sx={{ px: 1 }} />
								<TableCell sx={{ px: 1 }} />
							</TableRow>
						</TableHead>
					}
					tableBody={
						!hasRecords ? (
							<TableHasNoResults fetchState={fetchState} />
						) : (
							contacts.map((contact) => (
								<ContactsRow
									key={contact.id}
									id={contact.id}
									contact={contact}
									solutionStates={solutionStates}
									idkRegionsCodelist={idkRegionsCodelist}
									idkAgenciesCodelist={idkAgenciesCodelist}
								/>
							))
						)
					}
				>
					<PaginationRow
						fetchStorePath={fetchStorePath}
						colSpan={100}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</CollapsibleTable>
			</TableContainer>
		</ComponentLoader>
	);
};

ContactsTable.propTypes = {
	idkAgenciesCodelist: PropTypes.array,
	idkRegionsCodelist: PropTypes.array,
	solutionStates: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.idkRegions, 'idkRegionsCodelist'),
	codelistProvider(codelists.idkAgencies, 'idkAgenciesCodelist'),
	codelistProvider(codelists.solutionStateVirtual, 'solutionStates'),
	codelistProvider(codelists.solutionState),
)(ContactsTable);
